import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Phone, Mail, CheckCircle2, Info, ArrowRight, Wrench, Calendar, Calculator, Sparkles } from 'lucide-react'
import Lightbox from '../components/Lightbox'

const FALLBACK_VAERKTOEJ = [
  {
    id: "a9177674-195e-4cc0-93e7-74609ce2ee4b",
    title: { da: "Bosch Professional Nedbrydningshammer" },
    category: { da: "Håndværktøj & Nedbrydning" },
    price: { da: "Fra 150 kr./dag ekskl. moms" },
    images: [
      "https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/7e0fc6fa-1cd3-4cb9-a724-be2249d92538.jpg"
    ],
    slug: { da: "bosch-professional-nedbrydningshammer" },
    description: { da: "<p>Kraftfuld og professionel nedbrydningshammer til opbrydning af betongulve, fundamenter og murværk. Maskinen har en ekstrem høj slagstyrke, men er samtidig designet med vibrationsdæmpning, så den er behagelig at arbejde med i længere tid.</p><p>Leveres i praktisk transportkuffert med spidsmejsel og fladmejsel inkluderet i lejen.</p>" },
    specifications: { da: "<ul><li><strong>Slagenergi:</strong> 41 J</li><li><strong>Effekt:</strong> 1750 W</li><li><strong>Vægt:</strong> 16,5 kg</li><li><strong>Værktøjsholder:</strong> 28 mm sekskant</li><li><strong>Medfølger:</strong> 1 stk. spidsmejsel & 1 stk. fladmejsel</li></ul>" }
  },
  {
    id: "35402abe-3a18-4a0d-8cc5-a94d80936614",
    title: { da: "Stihl Benzindrevet Motorjordbor" },
    category: { da: "Have & Anlæg" },
    price: { da: "Fra 200 kr./dag ekskl. moms" },
    images: [
      "https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/91edf261-1889-4fa4-9347-dc80719d697f.jpg"
    ],
    slug: { da: "stihl-benzindrevet-motorjordbor" },
    description: { da: "<p>Professionelt benzindrevet jordbor til nem og hurtig boring af huller i jorden til f.eks. hegnspæle, stolper, træer eller punktfundamenter. Maskinen betjenes nemt af én person og er udstyret med QuickStop-borbremse for maksimal sikkerhed.</p><p>Vi udlejer boret med forskellige bor-diametre (100 mm, 150 mm eller 200 mm) alt efter dit behov.</p>" },
    specifications: { da: "<ul><li><strong>Motor:</strong> 2-takt benzin (Stihl 4-MIX)</li><li><strong>Effekt:</strong> 1,4 kW / 1,9 HK</li><li><strong>Vægt:</strong> 10,0 kg (uden bor)</li><li><strong>Bordiametre:</strong> Vælg mellem 100, 150 eller 200 mm</li><li><strong>Sikkerhed:</strong> QuickStop-borbremse</li></ul>" }
  },
  {
    id: "08249fde-f113-417f-b32a-7997935fbedc",
    title: { da: "Sigma Professionel Fliseskærer" },
    category: { da: "Flise- & Murerarbejde" },
    price: { da: "Fra 100 kr./dag ekskl. moms" },
    images: [
      "https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/63a1fca8-5db7-4d6f-bb78-db9bdae5f318.jpg"
    ],
    slug: { da: "sigma-professionel-fliseskaerer" },
    description: { da: "<p>Professionel manuel fliseskærer fra Sigma til præcis og hurtig skæring af keramiske fliser, klinker og mosaik. Fliseskæreren er kendt for sin ekstreme præcision og holdbarhed, og den kan klare selv meget hårde klinker.</p><p>Perfekt til badeværelsesprojekter, køkkenalrum eller terrassefliser.</p>" },
    specifications: { da: "<ul><li><strong>Maks. skærelængde:</strong> 77 cm</li><li><strong>Maks. diagonal skæring:</strong> 54 x 54 cm</li><li><strong>Maks. flisetykkelse:</strong> 20 mm</li><li><strong>Vægt:</strong> 11,5 kg</li><li><strong>Skæremetode:</strong> Træk-snit med fjedrende arbejdsbord</li></ul>" }
  }
]

const LOGO_URL = "https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/6fc95741-bbf3-49e6-9c69-766b0204a25f.png"

export default function VaerktojsUdlejning() {
  const [vaerktoej, setVaerktoej] = useState([])
  const [loading, setLoading] = useState(true)
  const [lightboxImages, setLightboxImages] = useState([])
  const [lightboxIndex, setLightboxIndex] = useState(null)

  // Booking form states
  const [selectedItem, setSelectedItem] = useState('')
  const [duration, setDuration] = useState('1_dag')
  const [calculatedPrice, setCalculatedPrice] = useState({ ex: 0, inc: 0, text: '' })
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    startDate: '',
    message: '',
    website_url: '' // Honeypot
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState({ success: null, message: '' })

  const openLightbox = (images, index) => {
    setLightboxImages(images)
    setLightboxIndex(index)
  }

  useEffect(() => {
    // Sæt meta-data
    document.title = "Udlejning af værktøj | D&W Udlejning ApS"
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Lej professionelt værktøj i Skibby. Vi udlejer nedbrydningshamre, motorjordbor, fliseskærere og meget mere til skarpe priser.')
    }

    fetch('https://api.dev.auxo.dk/public/content/vaerktoej')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente værktøj')
        return res.json()
      })
      .then((data) => {
        if (data && data.length > 0) {
          setVaerktoej(data)
        } else {
          setVaerktoej(FALLBACK_VAERKTOEJ)
        }
        setLoading(false)
      })
      .catch((err) => {
        console.error('Fejl ved hentning af værktøj:', err)
        setVaerktoej(FALLBACK_VAERKTOEJ)
        setLoading(false)
      })
  }, [])

  // Prisberegning
  useEffect(() => {
    if (!selectedItem) {
      setCalculatedPrice({ ex: 0, inc: 0, text: 'Vælg et værktøj for at se pris' })
      return
    }

    const item = vaerktoej.find(v => v.id === selectedItem)
    if (!item) return

    // Find basepris (f.eks. "Fra 150 kr./dag" -> 150)
    const priceText = item.price?.da || ''
    const match = priceText.match(/(\d[\d\s.]*)/)
    const basePrice = match ? parseInt(match[0].replace(/[.\s]/g, ''), 10) : 100

    let factor = 1
    let label = ''

    switch (duration) {
      case '1_dag':
        factor = 1
        label = '1 dag'
        break
      case '2_dage':
        factor = 1.8 // 10% rabat på dag 2
        label = '2 dage (inkl. 10% rabat)'
        break
      case 'weekend':
        factor = 1.5 // Weekendpris (fredag til mandag)
        label = 'Weekend (fredag til mandag)'
        break
      case '1_uge':
        factor = 4 // 1 uges leje (betal for 4 dage)
        label = '1 uge (7 dage - betal for 4)'
        break
      case 'andet':
        setCalculatedPrice({ ex: 0, inc: 0, text: 'Kontakt os for et skræddersyet tilbud' })
        return
      default:
        factor = 1
    }

    const exVat = Math.round(basePrice * factor)
    const incVat = Math.round(exVat * 1.25)

    setCalculatedPrice({
      ex: exVat,
      inc: incVat,
      text: `${exVat.toLocaleString('da-DK')} kr. ekskl. moms (${incVat.toLocaleString('da-DK')} kr. inkl. moms) for ${label}`
    })
  }, [selectedItem, duration, vaerktoej])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setSubmitStatus({ success: null, message: '' })

    const selectedTool = vaerktoej.find(v => v.id === selectedItem)
    const toolTitle = selectedTool ? selectedTool.title?.da : 'Ukendt værktøj'

    const payload = {
      ...formData,
      form_type: 'Værktøjsleje Forespørgsel',
      valgt_vaerktoej: toolTitle,
      lejeperiode: duration,
      beregnet_pris: calculatedPrice.ex > 0 ? calculatedPrice.text : 'Kontakt for pris'
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        setSubmitStatus({
          success: true,
          message: 'Tak for din forespørgsel! Vi vender tilbage til dig hurtigst muligt med en bekræftelse.'
        })
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          startDate: '',
          message: '',
          website_url: ''
        })
        setSelectedItem('')
      } else {
        throw new Error('Fejl ved indsendelse')
      }
    } catch (err) {
      setSubmitStatus({
        success: false,
        message: 'Der skete en fejl under afsendelsen. Prøv venligst igen eller ring til os direkte på 20 82 89 70.'
      })
    } finally {
      setSubmitting(false)
    }
  }

  const scrollToBooking = (toolId) => {
    setSelectedItem(toolId)
    const element = document.getElementById('booking-form-section')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <div className="bg-neutral-50 text-neutral-900 min-h-screen selection:bg-amber-500 selection:text-neutral-950">
      {/* INTRODUKTION SEKTION */}
      <section className="relative py-24 bg-white border-b border-neutral-200 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#f3f4f6_1px,transparent_1px),linear-gradient(to_bottom,#f3f4f6_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none opacity-60" />
        <div className="absolute right-[-5%] top-[-5%] w-[400px] h-[400px] pointer-events-none opacity-[0.03] select-none">
          <img src={LOGO_URL} alt="D&W Logo Vandmærke" className="w-full h-full object-contain" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <span className="inline-block text-xs font-bold text-amber-600 uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-md border border-amber-200">
            Værktøjsudlejning
          </span>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight text-neutral-900">
            Udlejning af <span className="text-amber-600">professionelt værktøj</span>
          </h1>
          <p className="text-neutral-600 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed">
            Har du brug for professionelt udstyr til dit næste murer-, have- eller nedbrydningsprojekt? Vi udlejer pålideligt og velholdt værktøj til både private og erhverv i Skibby og omegn.
          </p>
          <div className="pt-4 flex flex-wrap items-center justify-center gap-6 text-sm font-bold text-neutral-700">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-amber-500" />
              Skarpe dagspriser
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-amber-500" />
              Professionel kvalitet
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-amber-500" />
              Nem og hurtig booking
            </span>
          </div>
        </div>
      </section>

      {/* VÆRKTØJSLISTE */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:3rem_3rem] pointer-events-none opacity-40" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500" />
            </div>
          ) : (
            <div className="space-y-24">
              {vaerktoej.map((item) => (
                <div
                  key={item.id}
                  id={item.slug?.da}
                  className="scroll-mt-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start border-b border-neutral-200 pb-20 last:border-0 last:pb-0"
                >
                  {/* Venstre side: Billede */}
                  <div className="lg:col-span-6 space-y-4">
                    <button
                      onClick={() => openLightbox(item.images || [], 0)}
                      className="relative rounded-xl overflow-hidden border border-neutral-200 bg-white aspect-[4/3] shadow-sm w-full text-left focus:outline-none cursor-zoom-in group"
                      aria-label={`Forstør billede af ${item.title?.da}`}
                    >
                      <img
                        src={item.images?.[0]}
                        alt={item.title?.da}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      <span className="absolute top-4 left-4 bg-white/95 border border-neutral-200 text-amber-600 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md shadow-sm">
                        {item.category?.da}
                      </span>
                      <div className="absolute inset-0 bg-neutral-900/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                        <span className="px-4 py-2 rounded-md bg-white/95 border border-neutral-200 text-xs font-bold text-amber-600 uppercase tracking-wider shadow-sm">
                          Forstør billede
                        </span>
                      </div>
                    </button>
                  </div>

                  {/* Højre side: Detaljer */}
                  <div className="lg:col-span-6 space-y-6">
                    <div>
                      <h2 className="text-2xl sm:text-3xl font-black text-neutral-900 tracking-tight">
                        {item.title?.da}
                      </h2>
                      <div className="mt-2 flex items-baseline gap-2">
                        <span className="text-xs font-bold text-neutral-400 uppercase tracking-widest">Pris:</span>
                        <span className="text-xl font-black text-amber-600">{item.price?.da}</span>
                      </div>
                    </div>

                    {/* Beskrivelse */}
                    <div
                      className="prose prose-neutral max-w-none text-neutral-600 text-sm sm:text-base leading-relaxed space-y-4 [&_p]:leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: item.description?.da || '' }}
                    />

                    {/* Specifikationer */}
                    {item.specifications?.da && (
                      <div className="bg-white border border-neutral-200 p-5 rounded-xl space-y-3 shadow-sm">
                        <h3 className="text-sm font-bold text-neutral-800 uppercase tracking-wider flex items-center gap-2">
                          <Info className="w-4 h-4 text-amber-500" />
                          Tekniske specifikationer
                        </h3>
                        <div
                          className="prose prose-neutral max-w-none text-neutral-500 text-xs sm:text-sm space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mt-1 [&_strong]:text-neutral-700"
                          dangerouslySetInnerHTML={{ __html: item.specifications?.da }}
                        />
                      </div>
                    )}

                    {/* Book CTA */}
                    <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                      <button
                        onClick={() => scrollToBooking(item.id)}
                        className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-md bg-amber-500 hover:bg-amber-400 text-neutral-950 font-bold text-sm transition-all duration-200 shadow-md shadow-amber-500/10 cursor-pointer"
                      >
                        <Calendar className="w-4 h-4 shrink-0" />
                        <span>Send forespørgsel</span>
                      </button>
                      <a
                        href="tel:20828970"
                        className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-md bg-neutral-900 hover:bg-neutral-800 text-white font-bold text-sm transition-all duration-200"
                      >
                        <Phone className="w-4 h-4 shrink-0" />
                        <span>Ring og hør mere: 20 82 89 70</span>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* INTERAKTIV BOOKING SEKTION */}
      <section id="booking-form-section" className="relative py-24 bg-white border-t border-b border-neutral-200 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#f3f4f6_1px,transparent_1px),linear-gradient(to_bottom,#f3f4f6_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none opacity-60" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-12">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-600 uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-md border border-amber-200">
              <Calculator className="w-3.5 h-3.5" />
              Prisberegner & forespørgsel
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-neutral-900 tracking-tight">
              Send en uforpligtende <span className="text-amber-600">bookingforespørgsel</span>
            </h2>
            <p className="text-neutral-600 text-sm sm:text-base max-w-2xl mx-auto">
              Vælg det ønskede værktøj og lejeperiode nedenfor for at se den forventede pris med det samme. Udfyld derefter dine oplysninger, så vender vi hurtigt tilbage med en bekræftelse.
            </p>
          </div>

          <div className="bg-neutral-50 border border-neutral-200 rounded-2xl p-6 sm:p-10 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Spam-beskyttelse (Honeypot) - Skjult for mennesker */}
              <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
                <label htmlFor="website_url">Efterlad dette felt tomt</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleInputChange}
                  tabIndex="-1"
                  placeholder="http://"
                />
              </div>

              {/* TRIN 1: VÆLG VÆRKTØJ & PERIODE */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="selectedItem" className="block text-sm font-bold text-neutral-800 uppercase tracking-wider">
                    1. Vælg værktøj *
                  </label>
                  <select
                    id="selectedItem"
                    name="selectedItem"
                    required
                    value={selectedItem}
                    onChange={(e) => setSelectedItem(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-neutral-300 bg-white text-neutral-900 font-medium focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                  >
                    <option value="">-- Vælg værktøj --</option>
                    {vaerktoej.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.title?.da} ({item.price?.da})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="duration" className="block text-sm font-bold text-neutral-800 uppercase tracking-wider">
                    2. Vælg lejeperiode *
                  </label>
                  <select
                    id="duration"
                    name="duration"
                    required
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-neutral-300 bg-white text-neutral-900 font-medium focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                  >
                    <option value="1_dag">1 dag</option>
                    <option value="2_dage">2 dage (10% rabat)</option>
                    <option value="weekend">Weekend (fredag til mandag)</option>
                    <option value="1_uge">1 uge (7 dage - betal for 4)</option>
                    <option value="andet">Andet (skriv i bemærkninger)</option>
                  </select>
                </div>
              </div>

              {/* PRISVISNING */}
              <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <span className="text-xs font-bold text-amber-700 uppercase tracking-widest flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5" />
                    Beregnet pris
                  </span>
                  <p className="text-neutral-900 font-bold text-sm sm:text-base">
                    {calculatedPrice.text}
                  </p>
                </div>
                {calculatedPrice.ex > 0 && (
                  <div className="text-right shrink-0">
                    <span className="block text-2xl font-black text-amber-600">
                      {calculatedPrice.ex.toLocaleString('da-DK')} kr.
                    </span>
                    <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider">
                      Ekskl. moms / {calculatedPrice.inc.toLocaleString('da-DK')} kr. inkl. moms
                    </span>
                  </div>
                )}
              </div>

              {/* TRIN 2: KONTAKTOPLYSNINGER */}
              <div className="space-y-4 pt-4 border-t border-neutral-200">
                <h3 className="text-sm font-bold text-neutral-800 uppercase tracking-wider">
                  3. Dine oplysninger
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="block text-xs font-bold text-neutral-600 uppercase tracking-wider">
                      Dit navn *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-300 bg-white text-neutral-900 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                      placeholder="F.eks. Anders Andersen"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="company" className="block text-xs font-bold text-neutral-600 uppercase tracking-wider">
                      Firma (valgfrit)
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-300 bg-white text-neutral-900 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                      placeholder="F.eks. Entreprenør ApS"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-xs font-bold text-neutral-600 uppercase tracking-wider">
                      E-mailadresse *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-300 bg-white text-neutral-900 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                      placeholder="F.eks. anders@mail.dk"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="phone" className="block text-xs font-bold text-neutral-600 uppercase tracking-wider">
                      Telefonnummer *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-300 bg-white text-neutral-900 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                      placeholder="F.eks. 12 34 56 78"
                    />
                  </div>

                  <div className="space-y-2 sm:col-span-2">
                    <label htmlFor="startDate" className="block text-xs font-bold text-neutral-600 uppercase tracking-wider">
                      Ønsket startdato *
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      name="startDate"
                      required
                      value={formData.startDate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-300 bg-white text-neutral-900 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                    />
                  </div>

                  <div className="space-y-2 sm:col-span-2">
                    <label htmlFor="message" className="block text-xs font-bold text-neutral-600 uppercase tracking-wider">
                      Bemærkninger eller særlige ønsker (valgfrit)
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="4"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-300 bg-white text-neutral-900 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 resize-none"
                      placeholder="Skriv f.eks. hvis du ønsker levering, eller har spørgsmål til udstyret..."
                    />
                  </div>
                </div>
              </div>

              {/* FEJL / SUCCES MEDDELELSER */}
              {submitStatus.success !== null && (
                <div className={`p-4 rounded-lg border text-sm font-medium ${
                  submitStatus.success 
                    ? 'bg-green-50 border-green-200 text-green-800' 
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}>
                  {submitStatus.message}
                </div>
              )}

              {/* INDSEND KNAP */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:bg-amber-300 text-neutral-950 font-bold text-base transition-all duration-200 shadow-lg shadow-amber-500/10 cursor-pointer"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-neutral-950" />
                      <span>Sender forespørgsel...</span>
                    </>
                  ) : (
                    <>
                      <Calendar className="w-5 h-5" />
                      <span>Send bookingforespørgsel</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Lightbox til stor visning */}
      {lightboxIndex !== null && lightboxImages.length > 0 && (
        <Lightbox
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNext={() => setLightboxIndex((prev) => (prev + 1) % lightboxImages.length)}
          onPrev={() => setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length)}
        />
      )}
    </div>
  )
}
